import * as React from 'react'
import { useIntl } from 'react-intl'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { TableHead } from '@mui/material'
import ConfirmDelete from '../../../../../components/dialogs/ConfirmDelete'
import TablePaginationActions from '../../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { CourseExamState } from '../../../../../features/types'
import AddExamDialog from './AddExamDialog'
import { DEFAULT_PAGE_SIZE } from '../../../../../utils/constants'

interface Props {
    examsData: CourseExamState[]
    onDelete: (id: number) => void
}

const ExamListTable: React.FC<Props> = ({ examsData = [], onDelete }) => {
    const { formatMessage } = useIntl()
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_SIZE)

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0
            ? Math.max(0, (1 + page) * rowsPerPage - examsData.length)
            : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
            elevation={0}
        >
            <Table
                sx={{ minWidth: 500 }}
                aria-label="custom pagination table"
            >
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                        <TableCell>
                            {formatMessage({ id: 'titleKz' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleRu' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'startDateTime' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'endDateTime' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'edutestExamCode' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'examType' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'edit' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'delete' })}
                        </TableCell>

                    </TableRow>
                </TableHead>
                <TableBody>
                    {(rowsPerPage > 0
                        ? examsData.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                        )
                        : examsData
                    ).map((row) => (
                        <TableRow key={row?.id}>
                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.titleKz}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.titleRu}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.startDateTime}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.endDateTime}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.edutestExamCode}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {formatMessage({ id: row?.type === 'PKS' ? 'examTypePks' : 'examTypeBs' })}
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <AddExamDialog
                                    editMode={true}
                                    editCourseExam={row}
                                    courseId={row?.courseId}
                                />

                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <ConfirmDelete
                                    confirmAction={() => onDelete(row?.id)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}

                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={8} />
                        </TableRow>
                    )}
                </TableBody>

                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[
                                5,
                                10,
                                25,
                                { label: 'All', value: -1 },
                            ]}
                            colSpan={7}
                            count={examsData?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            sx={{ border: 0 }}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    )
}

export default ExamListTable
